import { create } from 'zustand';
import { message } from '@bfe/lux';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  Token,
  DynamicData,
  TradeOrder,
  OrderCount,
  TokenTradeCount,
  CoinIntro,
  TradeOrderParams,
  TradeOrderType,
  ShowMyOrder,
  MatchUserAsset,
  CoinBalance,
  CoinBalanceParams,
  AccountType,
  getTokenList,
  getTokenDetail,
  getTokenTradeCount,
  getDynamicData,
  getTradeOrders,
  getOrderCount,
  getCurrencyIntro,
  getCoinBalance,
  scoreTradeNoticeKey,
  getChartData,
  ChartDataParams,
  ChartData,
  getPartOrderConfig,
  PartOrderConfig,
  getHandicapTradeOrders,
} from '@/apis/market';
import {
  CreateTakerOrderParams,
  CreateMakerOrderParams,
  createTakerOrder,
  createMakerOrder,
  getPreferences,
  setPreferences,
} from '@/apis/order';
import {
  NewUserGuideReadKey,
  AgreementAgreedKey,
  LastVisitKey,
  marLastVisited,
  getLastVisited,
} from '@/app/biz';
import { gt } from '@/utils/number';
import axios, { Canceler } from 'axios';
import { makeOrderbook } from '@/utils/makeOrderbook';

type Market = {
  // banner
  orderCount?: OrderCount | undefined;
  fetchOrderCount: () => Promise<void>;
  fetchTradeCount: () => Promise<void>;
  coinIntro?: CoinIntro;
  fetchCurrencyIntro: (coin: string) => Promise<void>;
  // coins tabs
  tokenList?: Token[];
  tokenListAvaliable: Token[];
  tokenListPast: Token[];
  tokenTradeCount?: TokenTradeCount;
  fetchTokenList: (tokenName: string) => Promise<void>;
  fetchTokenListAvaliable: () => Promise<void>;
  fetchTokenListPast: () => Promise<void>;
  // price info
  dynamicData?: DynamicData;
  fetchDynamicData: (token?: string) => Promise<void>;
  // orders
  tradeOrders?: TradeOrder[];
  fetchTradeOrders: (params?: TradeOrderParams) => Promise<void>;
  tradeOrderLoading?: boolean;
  // balance
  balance?: CoinBalance;
  fetchCoinBalance: () => Promise<void>;
  // page
  currentInfo?: Token;
  setCurrentCoin: (tokenName: string) => void;
  fetchTokenDetail: (tokenName: string) => Promise<void>;
  currentTabName: string;
  setCurrentTabName: (tab: string) => void;
  currentCoin?: string;
  chartData:ChartData[];
  getChartData: (params:ChartDataParams,requestId:string) => Promise<void>;
  partOrderConfig:PartOrderConfig;
  getPartOrderConfig:(makerOrderId:number)=>Promise<void>;
};

type Order = {
  // taker order
  createTakerOrderResult?: any;
  createTakerOrder: (params: CreateTakerOrderParams) => Promise<any>;
  // maker order
  createMakerOrderResult?: any;
  createMakerOrder: (params: CreateMakerOrderParams) => Promise<any>;

  agreed?: boolean;
  setAgreed: (value: boolean) => void;
  lastVisitToken?: string;
  setLastVisitToken: (value: string) => void;
  newUserGuideRead?: boolean;
  setNewUserGuideRead: (value: boolean) => void;
  initConfig: () => Promise<void>;
};

interface Query {
  currentTab?: TradeOrderType;
  setCurrentTab: (key: TradeOrderType) => void;
  query?: Partial<TradeOrderParams>;
  setQuery: (query: Partial<TradeOrderParams>) => void;
  search: () => void;
  reset: () => void;
  init: () => void;

  showPagination?: boolean;
  pagination?: any;
  hasNext?: boolean;
  hasPrev?: boolean;
  firstItem?: any;
  setPagination: (pagination: any) => void;
}

type UI = {
  // newUserGuide
  displayNewUserGuide: boolean;
  setDisplayNewUserGuide: (value: boolean) => void;
  // maker order
  displayMakerOrder: boolean;
  setDisplayMakerOrder: (value: boolean) => void;
  // 控制riskNotice是否弹出
  riskNoticePop?: boolean;
  setRiskNoticePop: (value: boolean) => void;
  // 积分代币提示
  hiddenTradeNotice: boolean | undefined;
  setHiddenTradeNotice: () => void;
  // 创建订单按钮
  clickTradebtn: boolean;
  setClickTradebtn: (value: boolean) => void;
  // 创建订单 我要买入 我要卖出 按钮区分
  btnType?: string;
  setClickBtnType: (value: string) => void;
  // 我要买入 我要卖出 列表中被点击的单个数据
  buySell: any,
  setBuySell: (value: any) => void,
  // taker order
  displayTakerOrder: boolean;
  currentOrder?: TradeOrder;
  takerTradeOrderType?: TradeOrderType;
  setDisplayTakerOrder: (
    value: boolean,
    tradeOrder?: TradeOrderType,
    order?: TradeOrder
  ) => void;
  // fee modal
  displayFeeModal: boolean;
  setDisplayFeeModal: (value: boolean) => void;
};

const defaultQuery = {
  minPrice: '',
  maxPrice: '',
  minAmount: '',
  maxAmount: '',
  showMyOrder: ShowMyOrder.False,
  matchUserAsset: MatchUserAsset.False,
};

let cancelHandler: Canceler | null = null;

export const useMarket = create<Market & Query & Order & UI>()(
  subscribeWithSelector((set, get) => ({
    // 首页列表tab
    currentTabName: '1',
    setCurrentTabName: (tab) => {
      set(() => ({ currentTabName: tab }));
    },
    orderCount: undefined,
    fetchOrderCount: async () => {
      try {
        const { result = undefined } = (await getOrderCount()) || {};
        set(() => ({ orderCount: result, }));
      } catch (e) {
        console.error(e);
      }
    },
    coinIntro: undefined,
    fetchCurrencyIntro: async (tokenName: string) => {
      if (!tokenName) {
        return;
      }
      try {
        const { result = undefined } =
          (await getCurrencyIntro(tokenName)) || {};
        set(() => ({ coinIntro: result as any }));
      } catch (e) {
        console.error(e);
      }
    },
    // 首页金额数据统计
    tokenTradeCount: {
      itemsNum: 0,
      participantsNum: 0,
      totalAmount: ''
    },
    fetchTradeCount: async () => {
      try {
        const { result } = await getTokenTradeCount()
        set(() => ({
          tokenTradeCount: result,
        }));
      } catch (e) {
        console.error(e);
      }
    },
    tokenList: [],
    tokenListAvaliable: [],
    tokenListPast: [],
    fetchTokenListAvaliable: async () => {
      try {
        const { result = [] } = (await getTokenList('1')) || {};
        set(() => ({
          tokenListAvaliable: [...result]
        }));
      } catch (e) {
        console.error(e);
      }
    },
    fetchTokenListPast: async () => {
      try {
        const { result = [] } = (await getTokenList('2')) || {};
        set(() => ({
          tokenListPast: [...result]
        }));
      } catch (e) {
        console.error(e);
      }
    },
    // 准备删
    fetchTokenList: async (tab = '1') => {
      try {
        const { result = [] } = (await getTokenList(tab)) || {};
        if (tab === '1') {
          set(() => ({
            tokenList: [...result]
          }));
        }
      } catch (e) {
        console.error(e);
      }
    },
    dynamicData: undefined,
    fetchDynamicData: async (token?: string) => {
      if (!token) {
        return;
      }
      try {
        const { result = undefined } = (await getDynamicData(token)) || {};
        set(() => ({ dynamicData: result }));
      } catch (e) {
        console.error(e);
      }
    },
    // 是否展示积分提示
    hiddenTradeNotice: undefined,
    setHiddenTradeNotice: () => {
      if (typeof window !== 'undefined' && window.localStorage.getItem(scoreTradeNoticeKey) === 'true') {
        set(() => ({ hiddenTradeNotice: true }));
      }
      else {
        set(() => ({ hiddenTradeNotice: false }));
      }
    },
    // 积分交易弹窗条件
    clickTradebtn: false,
    setClickTradebtn: (newState) => {
      set(() => ({ clickTradebtn: newState }));
    },
    // 按钮类型
    btnType: '',
    setClickBtnType: (newState) => {
      set(() => ({ btnType: newState }));
    },
    // 我要卖入我要卖出 被点击数据
    buySell: undefined,
    setBuySell: (newState) => {
      set(() => ({ buySell: newState }));
    },
    riskNoticePop: false,
    setRiskNoticePop: (newState) => {
      set(() => ({ riskNoticePop: newState }));
    },
    tradeOrders: [],
    tradeOrderLoading: false,
    fetchTradeOrders: async (newParams?: TradeOrderParams) => {
      const store = get();
      const params = {
        ...store.query,
        ...newParams,
        orderType: newParams?.orderType || store.currentTab,
        tokenName: newParams?.tokenName || store.currentCoin,
      };
      //盘口模式不分页
      if(params.orderType !== TradeOrderType.OrderBook){
        params.pageSize = 11
      }
      if (!params.tokenName || params.tokenName === 'undefined') {
        return;
      }
      set(() => ({ tradeOrderLoading: true }));
      try {
        if(params.orderType === TradeOrderType.OrderBook){
          const { result = [] } =
          (await getHandicapTradeOrders(params as TradeOrderParams)) || {};
          set(() => ({
            tradeOrders: makeOrderbook(result),
            tradeOrderLoading: false,
          }));
        }else{
          const { result = [] } =
          (await getTradeOrders(params as TradeOrderParams)) || {};
          const backward = newParams?.direction == 2;
          const forward = newParams?.direction == 1;
          const hasNext = result.length === 11 || forward;
          const originList = store.tradeOrders || [];
          const list = forward
            ? [...(result || []), ...originList].slice(0, 10)
            : (result || []).slice(0, 10);
          const firstItem = !newParams?.direction ? list?.[0] : store.firstItem;
          const hasPrev = firstItem?.orderId != list?.[0]?.orderId;

          set(() => ({
            tradeOrders: list,
            hasNext,
            hasPrev,
            firstItem,
            tradeOrderLoading: false,
          }));
        }
      } catch (e) {
        console.error(e);
        set(() => ({ tradeOrderLoading: false }));
      }
    },
    // 获取币信息介绍与订单
    setCurrentCoin: (tokenName) => {
      const store = get();
      store.fetchCurrencyIntro(tokenName);
      store.fetchTradeOrders({ tokenName } as TradeOrderParams);
      set(() => ({ currentCoin: tokenName }));
    },
    // 币对详情
    currentInfo: undefined,
    // coin balance
    balance: undefined,
    fetchTokenDetail: async (tokenName) => {
      if (!tokenName) return
      try {
        const { result = undefined } = (await getTokenDetail(tokenName)) || {}
        set(() => ({ currentInfo: result }));
      } catch (e) {
        console.error(e);
      }
    },
    fetchCoinBalance: async () => {
      const info = get().currentInfo;
      if (!info?.settleToken) {
        return;
      }
      try {
        const params: CoinBalanceParams = {
          accountType: AccountType.ACCOUNT_TYPE_UNIFIED,
          sCoin: info?.settleToken,
        };
        const { result = undefined } = (await getCoinBalance(params)) || {};
        set(() => ({ balance: result }));
      } catch (e) {
        console.error(e);
      }
    },
    // tab
    currentTab: TradeOrderType.Sell,
    setCurrentTab: (key: TradeOrderType) => {
      set({ currentTab: key });
      const store = get();
      store.setQuery({
        ...defaultQuery,
        orderType: key,
      } as any);

      store.fetchTradeOrders({
        ...defaultQuery,
        orderType: key,
      } as TradeOrderParams);
    },
    // query
    query: undefined,
    setQuery: (query: Partial<TradeOrderParams>) => {
      const store = get();
      const originQuery = get().query;
      const nextQuery = Object.entries(query)
        .filter(([key, value]) => {
          return value != null;
        })
        .reduce((acc, item) => {
          const key = item[0];
          const value = item[1];
          return { ...acc, [key]: value };
        }, {});
      const newQuery = {
        ...defaultQuery,
        ...originQuery,
        ...nextQuery,
        orderType: store.currentTab,
      } as TradeOrderParams;

      // store.fetchTradeOrders(newQuery as TradeOrderParams);
      set(() => ({ query: newQuery }));
    },
    search: async (newParams?: Partial<TradeOrderParams>) => {
      const store = get();
      const originQuery = get().query;

      const nextQuery = { ...newParams };

      // 大小排序
      if (originQuery?.minPrice && originQuery?.maxPrice) {
        if (gt(originQuery.minPrice, originQuery.maxPrice)) {
          nextQuery['minPrice'] = originQuery.maxPrice;
          nextQuery['maxPrice'] = originQuery.minPrice;
        }
      }

      if (originQuery?.minAmount && originQuery?.maxAmount) {
        if (gt(originQuery.minAmount, originQuery.maxAmount)) {
          nextQuery['minAmount'] = originQuery.maxAmount;
          nextQuery['maxAmount'] = originQuery.minAmount;
        }
      }

      if (nextQuery && Object.keys(nextQuery).length > 0) {
        set(() => ({
          query: { ...store.query, ...nextQuery },
        }));
      }

      const newQuery = {
        ...defaultQuery,
        ...originQuery,
        ...nextQuery,
        orderType: store.currentTab,
      } as TradeOrderParams;

      store.fetchTradeOrders(newQuery as TradeOrderParams);
    },
    // reset
    reset: async () => {
      const store = get();
      store.fetchTradeOrders(defaultQuery as TradeOrderParams);

      set(() => ({
        query: { ...store.query, ...defaultQuery },
      }));
    },
    // action
    init: async () => {
      const newQuery = {
        orderType: TradeOrderType.Buy,
        showMyOrder: ShowMyOrder.False,
        matchUserAsset: MatchUserAsset.False,
      };
      // const lastVisit = getLastVisited()
      set(() => ({ query: newQuery }));
      get().initConfig();
    },
    // ui
    displayNewUserGuide: false,
    setDisplayNewUserGuide: (newState) => {
      set(() => ({ displayNewUserGuide: newState }));
    },
    displayMakerOrder: false,
    setDisplayMakerOrder: (newState) => {
      const store = get();
      if (newState) {
        store.fetchOrderCount();
        store.fetchCoinBalance();
        // store.fetchTokenList(true);
      }
      set(() => ({ displayMakerOrder: newState }));
    },
    displayTakerOrder: false,
    takerTradeOrderType: undefined,
    setDisplayTakerOrder: (
      newState,
      tradeOrder?: TradeOrderType,
      order?: TradeOrder
    ) => {
      const store = get();
      if (newState) {
        store.fetchOrderCount();
        store.fetchCoinBalance();
      }
      set(() => ({
        displayTakerOrder: newState,
        takerTradeOrderType: tradeOrder,
        currentOrder: order,
      }));
    },
    displayFeeModal: false,
    setDisplayFeeModal: (newState) => {
      set(() => ({ displayFeeModal: newState }));
    },
    createTakerOrder: async (params) => {
      try {
        const {
          result = undefined,
          ret_code,
          ret_msg,
        } = (await createTakerOrder(params)) || {};
        if (ret_code === 0) {
          // message.success('');
          set(() => ({ createTakerOrderResult: result }));
          get().fetchTradeOrders();
          return true;
        } else {
          // kyc
          if (ret_code === 10024) {
            return;
          }
          message.error(ret_msg);
        }
      } catch (e) {
        console.error(e);
      }
    },
    createMakerOrder: async (params) => {
      try {
        const {
          result = undefined,
          ret_code,
          ret_msg,
        } = (await createMakerOrder(params)) || {};
        if (ret_code === 0) {
          set(() => ({ createMakerOrderResult: result }));
          if(get().currentTab === TradeOrderType.OrderBook){
            setTimeout(() => {
              get().fetchTradeOrders();
            }, 1000);
          }else{
            get().fetchTradeOrders();
          }
          return true;
        } else {
          // max pending orders
          if (ret_code === 20008) {
            return ret_code;
          }
          // kyc
          if (ret_code === 10024) {
            return;
          }
          message.error(ret_msg);
        }
      } catch (e) {
        console.error(e);
      }
    },
    agreed: false,
    setAgreed: (newState) => {
      // setAgreementAgreed(newState);
      setPreferences({ [AgreementAgreedKey]: `${newState}` });
      set(() => ({ agreed: newState }));
    },
    lastVisitToken: undefined,
    setLastVisitToken: (tokenName: string) => {
      marLastVisited(tokenName);
      // setPreferences({ [LastVisitKey]: tokenName });
      // set(() => ({ lastVisitToken: tokenName }));
    },
    newUserGuideRead: undefined,
    setNewUserGuideRead: () => {
      // markNewUserGuideReadStatus();
      setPreferences({ [NewUserGuideReadKey]: 'true' });
      set(() => ({ newUserGuideRead: true }));
    },
    initConfig: async () => {
      // local
      const lastVisit = getLastVisited();

      const { result } = await getPreferences([
        AgreementAgreedKey,
        NewUserGuideReadKey,
        LastVisitKey,
      ]);

      set(
        () =>
        ({
          newUserGuideRead: result?.preferences?.[NewUserGuideReadKey],
          lastVisitToken: lastVisit,
          agreed: result?.preferences?.[AgreementAgreedKey] === 'true',
        } as any)
      );
    },
    showPagination: false,
    hasNext: undefined,
    hasPrev: undefined,
    firstId: undefined,
    pagination: undefined,
    setPagination: (pagination) => {
      const state = get();
      const origin = state?.pagination;
      const isForward = pagination?.direction == 1;
      const isFilled = state?.currentTab === TradeOrderType.Filled;
      const data = state?.tradeOrders || [];
      const startItem = isForward && data.length > 1 ? data[1] : data[0];
      const endItem = data[data.length - 1];
      // debugger

      const priceOrTradeTime = isForward
        ? isFilled
          ? startItem?.tradeTime
          : startItem?.price
        : isFilled
          ? endItem?.tradeTime
          : endItem?.price;

      const params = {
        ...pagination,
        fromId: isForward ? startItem?.orderId : endItem?.orderId,
        priceOrTradeTime: priceOrTradeTime,
      };
      state.fetchTradeOrders(params);
      set(() => ({
        pagination: {
          ...params,
          startId: startItem?.orderId,
          endId: endItem?.orderId,
        },
      }));
    },
    chartData: [],
    getChartData: async (params:ChartDataParams) => {
      try {

        if(cancelHandler){
          cancelHandler();
        }

        const cancelToken = new axios.CancelToken(function (cancel){
          cancelHandler = cancel;
        });
        const res = (await getChartData(params,{
          cancelToken:cancelToken
        })) || {};

        if(res.success){
          set(() => ({ chartData: res.result || [] }));
        }else{
          console.error('getChartData:',res.message);
          set(() => ({ chartData: [] }));
        }
      } catch (e) {
        console.error(e);
      }finally{
        cancelHandler = null;
      }
    },
    partOrderConfig:{partOrderMinTradeQuantity:''},
    getPartOrderConfig: async (makerOrderId: number) => {
      try {
          const res = (await getPartOrderConfig(makerOrderId)) || {};
          if(res.success){
            set(() => ({ partOrderConfig: res.result}));
          }else{
            console.error('getOrderDetails:',res.message);
            set(() => ({ partOrderConfig: {partOrderMinTradeQuantity:''} }));
          }
        } catch (e) {
          console.error(e);
        }
    }
  }))
);
